import type { SpriteDefinition } from '@/render/spriteBaker';
import type { Facing } from '@/sprites/katie';
import { PALETTE } from '@/sprites/palette';
import type { ToolFamily } from '@/world/toolbelt';

/**
 * The tool in Katie's hand: a 16x16 overlay drawn on her own grid.
 *
 * Aligned to her sprite rather than offset, so positioning is "same place as
 * Katie" and nothing has to stay in sync as her art changes. It also means the
 * tool is mirrored by the same flip that mirrors her, so a left-facing hoe
 * needs no art of its own — the same trick her own sprites use.
 *
 * Facing away gets its own grid rather than reusing the front one. The overlay
 * is drawn behind her when she faces up, which is the correct depth — but at
 * her side the shaft then falls entirely within her silhouette and she reads as
 * empty-handed. The back-facing grids hold the tool a little further out, clear
 * of her shoulders, so "she is carrying a hoe" survives the occlusion.
 *
 * Colours are the icons' colours: trunk brown for handles, stone grey for iron,
 * apron white for line. No new palette entries.
 */

export type HeldFacing = 'down' | 'up' | 'side';

/** Left is right, flipped at draw time — exactly as Katie herself works. */
export function heldFacing(facing: Facing): HeldFacing {
  if (facing === 'left' || facing === 'right') return 'side';
  return facing;
}

/** Held low at her side, shaft down, head on the ground beside her boot. */
const HOE_DOWN: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '............m...',
  '............m...',
  '............m...',
  '............m...',
  '............m...',
  '............m...',
  '............m...',
  '............m...',
  '...........rrr..',
  '...........RRR..',
  '................',
];

/** Same carry, held out past her shoulder so her back does not swallow it. */
const HOE_UP: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '..............m.',
  '..............m.',
  '..............m.',
  '..............m.',
  '..............m.',
  '..............m.',
  '..............m.',
  '..............m.',
  '.............rrr',
  '.............RRR',
  '................',
];

/** Angled forward, the way you carry a hoe walking to the row. */
const HOE_SIDE: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '.........m......',
  '..........m.....',
  '...........m....',
  '............m...',
  '.............rrr',
  '................',
];

const CAN_DOWN: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '.............r..',
  '..........rrrr..',
  '..........rRRr..',
  '..........rRRr..',
  '..........rrrr..',
  '................',
  '................',
];

const CAN_UP: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '...............r',
  '............rrrr',
  '............rRRr',
  '............rRRr',
  '............rrrr',
  '................',
  '................',
];

const CAN_SIDE: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '............rrrr',
  '...........rRRRr',
  '...........rRRRr',
  '...........rrrrr',
  '................',
  '................',
];

/** Shouldered, tip up, line hanging free of her body. */
const ROD_DOWN: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '............m.w.',
  '............m.w.',
  '............m.w.',
  '............m.w.',
  '............m.w.',
  '............m.w.',
  '............m.w.',
  '............m.w.',
  '............m.w.',
  '............M...',
  '................',
  '................',
];

const ROD_UP: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '.............m.w',
  '.............m.w',
  '.............m.w',
  '.............m.w',
  '.............m.w',
  '.............m.w',
  '.............m.w',
  '.............m.w',
  '.............m.w',
  '.............M..',
  '................',
  '................',
];

const ROD_SIDE: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '..............m.',
  '.............m.w',
  '............m..w',
  '...........m...w',
  '..........m....w',
  '.........M.....w',
  '...............w',
  '...............w',
  '................',
  '................',
];

/** Carried point-down at her side, the way you hold a blade you are not using. */
const SWORD_DOWN: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '............m...',
  '...........rrr..',
  '............r...',
  '............r...',
  '............r...',
  '............r...',
  '............r...',
  '............r...',
  '............r...',
  '............r...',
  '................',
  '................',
];

const SWORD_UP: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '..............m.',
  '.............rrr',
  '..............r.',
  '..............r.',
  '..............r.',
  '..............r.',
  '..............r.',
  '..............r.',
  '..............r.',
  '..............r.',
  '................',
  '................',
];

/** Levelled ahead of her, which is also where the swing will come from. */
const SWORD_SIDE: readonly string[] = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '.....mmr........',
  '.....mmrrrrrrr..',
  '.....mmr........',
  '................',
  '................',
  '................',
];

function build(name: string, rows: readonly string[]): SpriteDefinition {
  return { name, rows, palette: PALETTE };
}

export const HELD_TOOL_SPRITES = {
  'hoe-down': build('held-hoe-down', HOE_DOWN),
  'hoe-up': build('held-hoe-up', HOE_UP),
  'hoe-side': build('held-hoe-side', HOE_SIDE),
  'wateringCan-down': build('held-can-down', CAN_DOWN),
  'wateringCan-up': build('held-can-up', CAN_UP),
  'wateringCan-side': build('held-can-side', CAN_SIDE),
  'rod-down': build('held-rod-down', ROD_DOWN),
  'rod-up': build('held-rod-up', ROD_UP),
  'rod-side': build('held-rod-side', ROD_SIDE),
  'sword-down': build('held-sword-down', SWORD_DOWN),
  'sword-up': build('held-sword-up', SWORD_UP),
  'sword-side': build('held-sword-side', SWORD_SIDE),
} as const satisfies Record<`${ToolFamily}-${HeldFacing}`, SpriteDefinition>;

export type HeldToolSpriteKey = keyof typeof HELD_TOOL_SPRITES;

export function heldToolSpriteKey(family: ToolFamily, facing: Facing): HeldToolSpriteKey {
  return `${family}-${heldFacing(facing)}`;
}
